import React, { useMemo } from "react";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryParam } from "@/hooks/use-query-param";
import { ArrowLeft } from "lucide-react";
import moment from "moment-timezone";
import {
  DestinationFetchRunImproved,
} from "@/lib/api/destinationHoursImproved";
import {
  useGetRunContent,
  ActualHours,
  RawHours,
} from "@/lib/api/destinationHoursImproved/runInfo";
import { CommonDestinationTimezone } from "@/lib/utils";

interface RunHoursViewProps {
  onBackClick: () => void;
}

interface RunHourWithLocation extends ActualHours {
  location_name: string;
  location_link: string;
  service_category_name: string;
}

export function RunHoursView({ onBackClick }: RunHoursViewProps) {
  const [runId, setSelectedRunId] = useQueryParam("run_id", "");

  // Get run content data using the new API
  const runContentQuery = useGetRunContent(runId!);

  // Shared transformation function
  const transformHoursData = (hoursData: (ActualHours | RawHours)[]): RunHourWithLocation[] => {
    return hoursData.map((hour: ActualHours | RawHours) => ({
      ...hour,
      location_name: hour.location.name,
      location_link: hour.location.link || "#",
      service_category_name: hour.serviceCategory?.service_name || "No Category",
    } as RunHourWithLocation)).sort((a: RunHourWithLocation, b: RunHourWithLocation) => {
      // Sort by service category, then date
      if (a.service_category_name !== b.service_category_name) {
        return a.service_category_name.localeCompare(b.service_category_name);
      }
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  };

  // Transform actual hours data
  const actualHoursData = useMemo((): RunHourWithLocation[] => {
    if (!runContentQuery.data?.data) return [];
    const { actualHours } = runContentQuery.data.data || { actualHours: [] };
    return transformHoursData(actualHours);
  }, [runContentQuery.data]);

  // Transform raw hours data
  const rawHoursData = useMemo((): RunHourWithLocation[] => {
    if (!runContentQuery.data?.data) return [];
    const { rawHours } = runContentQuery.data.data || { rawHours: [] };
    return transformHoursData(rawHours);
  }, [runContentQuery.data]);

  // Transform previous run data
  const previousRawHoursData = useMemo((): RunHourWithLocation[] => {
    if (!runContentQuery.data?.data?.previousRun) return [];
    const { rawHours } = runContentQuery.data.data.previousRun;
    return transformHoursData(rawHours);
  }, [runContentQuery.data]);

  const previousActualHoursData = useMemo((): RunHourWithLocation[] => {
    if (!runContentQuery.data?.data?.previousRun) return [];
    const { actualHours } = runContentQuery.data.data.previousRun;
    return transformHoursData(actualHours);
  }, [runContentQuery.data]);

  const columns = useMemo((): DataTableColumn<RunHourWithLocation>[] => [
    {
      id: "location_name",
      accessorKey: "location_name",
      header: "Location",
      cell: (info) => {
        const locationName = info.getValue() as string;
        const locationLink = (info.row.original as RunHourWithLocation).location_link;
        return (
          <a
            href={locationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {locationName}
          </a>
        );
      },
      minSize: 150,
      maxSize: 200,
      enableSorting: true,
      enableGrouping: true,
    },
    {
      id: "service_category_name",
      accessorKey: "service_category_name",
      header: "Service Category",
      cell: (info) => info.getValue() as string,
      minSize: 120,
      maxSize: 180,
      enableSorting: true,
      enableGrouping: true,
    },
    {
      id: "date",
      accessorKey: "date",
      header: "Date",
      cell: (info) => moment(info.getValue() as string).format("MMM DD, YYYY"),
      minSize: 110,
      maxSize: 130,
      enableSorting: true,
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: (info) => info.getValue() as string,
      minSize: 100,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "open_time",
      accessorKey: "open_time",
      header: "Open Time",
      cell: (info) => {
        const timeStr = info.getValue() as string;
        if (!timeStr) return "--";
        return moment(timeStr, "HH:mm").format("hh:mm a");
      },
      minSize: 120,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "close_time",
      accessorKey: "close_time",
      header: "Close Time",
      cell: (info) => {
        const timeStr = info.getValue() as string;
        if (!timeStr) return "--";
        return moment(timeStr, "HH:mm").format("hh:mm a");
      },
      minSize: 120,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "instructions_note",
      accessorKey: "instructions_note",
      header: "Instructions Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "global_note",
      accessorKey: "global_note",
      header: "Global Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "grade_note",
      accessorKey: "grade_note",
      header: "Grade Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "other_note",
      accessorKey: "other_note",
      header: "Other Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "closure_reason",
      accessorKey: "closure_reason",
      header: "Closure Reason",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 150,
      maxSize: 200,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
  ], []);

  const isLoading = runContentQuery.isLoading;
  const run = runContentQuery?.data?.data?.run;
  const previousRun = runContentQuery?.data?.data?.previousRun?.run;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onBackClick}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Runs
        </Button>
        <div>
          <h2 className="text-xl font-bold">Hours for Run</h2>
          <p className="text-sm text-muted-foreground">
            {run ?
              `Run started: ${moment(run.run_started_at).format("MMM DD, YYYY hh:mm a")}` :
              <Skeleton className="h-3 w-48" />
            }
          </p>
        </div>
      </div>

      {/* Unified Run Info Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Run Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {run ? (
            <>
              <div>
                <h4 className="font-medium text-sm mb-2">Current Run</h4>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Status:</span>{" "}
                    <Badge
                      variant={
                        run.run_status === "completed" ? "default" :
                          run.run_status === "failed" ? "destructive" :
                            run.run_status === "running" ? "secondary" : "outline"
                      }
                      className="text-xs"
                    >
                      {run.run_status}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Started:</span>{" "}
                    <div className="font-medium">{moment.tz(run.run_started_at, CommonDestinationTimezone).format("MMM DD, hh:mm a")}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Completed:</span>{" "}
                    <div className="font-medium">
                      {run.run_completed_at
                        ? moment.tz(run.run_completed_at, CommonDestinationTimezone).format("MMM DD, hh:mm a")
                        : "Not completed"
                      }
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Found Hours:</span>{" "}
                    <div className="font-medium">{run.raw_hour_ids.length}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Found Changed:</span>{" "}
                    <div className="font-medium">{run.changed_hour_ids.length}</div>
                  </div>
                </div>
                {run.error_message && (
                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                    <span className="font-medium text-red-600 text-xs">Error:</span>
                    <p className="text-xs text-red-600 mt-1">{run.error_message}</p>
                  </div>
                )}
              </div>

              {previousRun && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-sm mb-2">Previous Run</h4>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground">Status:</span>{" "}
                      <Badge
                        variant={
                          previousRun.run_status === "completed" ? "default" :
                            previousRun.run_status === "failed" ? "destructive" :
                              previousRun.run_status === "running" ? "secondary" : "outline"
                        }
                        className="text-xs"
                      >
                        {previousRun.run_status}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Started:</span>{" "}
                      <div className="font-medium">{moment.tz(previousRun.run_started_at, CommonDestinationTimezone).format("MMM DD, hh:mm a")}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Completed:</span>{" "}
                      <div className="font-medium">
                        {previousRun.run_completed_at
                          ? moment.tz(previousRun.run_completed_at, CommonDestinationTimezone).format("MMM DD, hh:mm a")
                          : "Not completed"
                        }
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Found Hours:</span>{" "}
                      <div className="font-medium">{previousRun.raw_hour_ids.length}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Found Changed:</span>{" "}
                      <div className="font-medium">{previousRun.changed_hour_ids.length}</div>
                    </div>
                  </div>
                  {previousRun.error_message && (
                    <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                      <span className="font-medium text-red-600 text-xs">Error:</span>
                      <p className="text-xs text-red-600 mt-1">{previousRun.error_message}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
              <div><Skeleton className="h-3 w-20" /></div>
              <div><Skeleton className="h-3 w-24" /></div>
              <div><Skeleton className="h-3 w-22" /></div>
              <div><Skeleton className="h-3 w-16" /></div>
              <div><Skeleton className="h-3 w-18" /></div>
              <div><Skeleton className="h-3 w-16" /></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actual Hours Table */}
      {actualHoursData.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Changed Hours ({actualHoursData.length} records)</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              className="shadow-lg"
              stickyHeaders
              tableClassName="max-h-[calc(50vh-200px)] overflow-y-scroll"
              data={actualHoursData}
              columns={columns}
              enableSorting
              enableMultiSort
              enableFiltering
              enableGrouping
              enableColumnVisibility
              enableExport
              filterStyle="select"
              filterMode="internal"
              groupBy={["service_category_name"]}
              emptyMessage={isLoading ? "Loading changed hours..." : "No changed hours found"}
            />
          </CardContent>
        </Card>
      ) : (
        !isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Changed Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-4">
                No changed hours available for this run.
              </p>
            </CardContent>
          </Card>
        )
      )}

      {/* Raw Hours Table */}
      <Card>
        <CardHeader>
          <CardTitle>Found Hours ({rawHoursData.length} records)</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            className="shadow-lg"
            stickyHeaders
            tableClassName="max-h-[calc(50vh-200px)] overflow-y-scroll"
            data={rawHoursData}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableExport
            filterStyle="select"
            filterMode="internal"
            groupBy={["service_category_name"]}
            emptyMessage={isLoading ? "Loading found hours..." : "No found hours found"}
          />
        </CardContent>
      </Card>

      {/* Previous Run Hours Tables */}
      {previousRun && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Previous Hours ({previousRawHoursData.length} records)</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                className="shadow-lg"
                stickyHeaders
                tableClassName="max-h-[calc(50vh-200px)] overflow-y-scroll"
                data={previousRawHoursData}
                columns={columns}
                enableSorting
                enableMultiSort
                enableFiltering
                enableGrouping
                enableColumnVisibility
                enableExport
                filterStyle="select"
                filterMode="internal"
                groupBy={["service_category_name"]}
                emptyMessage="No found hours found for previous run"
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
